import { ManagerEventsIndexEvent } from "@/types/manager_events";
import { Badge, Box, DropdownMenu, Flex, IconButton, Text } from "@radix-ui/themes";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { Camera, Car, ClipboardList, EllipsisVertical, MapPin, Calendar, Users, Users2 } from "lucide-react";
import { dateRangeFilter } from "@/shared/tables/utility";
import { badgeColors } from "@/shared/constants/constants";
import BasicTable from "../BasicTable";

interface Props {
  events: ManagerEventsIndexEvent[];
  setSelectedRow: React.Dispatch<
    React.SetStateAction<ManagerEventsIndexEvent | null>
  >;
}

export default function EventsTable({ events, setSelectedRow }: Props) {
  const [data, setData] = useState(() => [...events]);

  useEffect(() => {
    setData([...events]);
  }, [events]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columnHelper = createColumnHelper<ManagerEventsIndexEvent>();

  const columns = useMemo<ColumnDef<ManagerEventsIndexEvent, any>[]>(
    () => [
      columnHelper.accessor("track_name", {
        header: "Track",
        accessorFn: (row) => row.track_name,
        meta: { filterVariant: "text" },
        cell: (props) => (
          <Text>{props.row.original.track_name}</Text>
        ),
      } as AccessorFnColumnDef<ManagerEventsIndexEvent, any>),
      columnHelper.accessor("name", {
        header: "Event",
        meta: { filterVariant: "text" },
        cell: (props) => (
          <Text>{props.row.original.name}</Text>
        ),
      } as AccessorFnColumnDef<ManagerEventsIndexEvent, any>),
      columnHelper.accessor("date_raw", {
        header: "Date",
        cell: (props) => props.row.original.date,
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
      columnHelper.accessor("publish_at", {
        header: "Publish At",
        accessorFn: (row) => row.is_published,
        cell: (props) =>
          props.getValue() ? (
            <Badge size="2" variant="soft" color={badgeColors["published"]}>
              <Text as="p">Published</Text>
            </Badge>
          ) : props.row.original.publish_at === null ? (
            <Badge size="2" variant="soft" color={badgeColors["draft"]}>
              <Text as="p">Draft</Text>
            </Badge>
          ) : (
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.row.original.publish_at}
            </Text>
          ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "true", title: "Open" },
            { value: "false", title: "Closed" },
          ],
        },
      } as AccessorFnColumnDef<ManagerEventsIndexEvent, any>),
      columnHelper.accessor("applications_open", {
        header: "Status",
        accessorFn: (row) => (row.applications_open ? "true" : "false"),
        cell: (props) => (
          <Badge
            size="2"
            variant="soft"
            color={badgeColors[props.getValue() ? "open" : "closed"]}
          >
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue() ? "Open" : "Closed"}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "true", title: "Open" },
            { value: "false", title: "Closed" },
          ],
        },
      } as AccessorFnColumnDef<ManagerEventsIndexEvent, any>),
      columnHelper.accessor("accepts_media", {
        header: "Accepts",
        cell: (props) => {
          const accepts = [
            props.row.original.accepts_media && {
              label: "Media",
              icon: <Camera size={13} />,
            },
            props.row.original.accepts_staff && {
              label: "Staff",
              icon: <Users size={13} />,
            },
            props.row.original.accepts_drivers && {
              label: "Drivers",
              icon: <Car size={13} />,
            },
            props.row.original.accepts_teams && {
              label: "Teams",
              icon: <Users2 size={13} />,
            },
          ].filter(Boolean) as { label: string; icon: React.ReactNode }[];

          return (
            <Flex gap="2" wrap="wrap">
              {accepts.map(({ label, icon }) => (
                <Badge key={label} color="gray" variant="soft" size="2">
                  <Flex align="center" gap="1">
                    {icon}
                    {label}
                  </Flex>
                </Badge>
              ))}
            </Flex>
          );
        },
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "true", title: "Yes" },
            { value: "false", title: "No" },
          ],
        },
      } as AccessorFnColumnDef<ManagerEventsIndexEvent, any>),
      columnHelper.accessor("total_applications", {
        header: "Applicants",
        cell: (props) => props.getValue(),
        meta: {
          filterVariant: "number"
        }
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (props) => (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton variant="ghost" size="1" onClick={(e) => e.stopPropagation()}>
                <EllipsisVertical size="16px" />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {/* Only where the section serves the event's own page. */}
              {props.row.original.manage_path && (
                <DropdownMenu.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = props.row.original.manage_path!;
                  }}
                >
                  <ClipboardList size="14px" />
                  Manage Applications
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/tracks/${props.row.original.track_slug}`;
                }}
              >
                <MapPin size="14px" />
                View Track
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = props.row.original.show_path;
                }}
              >
                <Calendar size="14px" />
                View Event
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <BasicTable
      table={table}
      handleRowOnClick={(row) => setSelectedRow(row.original)}
    />
  );
}
